import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Credit, Person, PersonRole } from 'types/Asset';
import icons from 'assets/images/icons';
import { PictureWithFallback } from 'components/Picture/PictureWithFallback';
import fallbackImage from 'assets/images/hero_fallback.png';
import translate from 'utils/fnTranslate';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { DetailMarkerContainer, DetailMarkerAndMetaData } from 'style/styled-components/reusable.css';
import { DetailMoreModal } from 'components/DetailMoreModal/DetailMoreModal';
import { useApp } from 'providers/useApp/AppContext';
import SVGInline from 'components/SVGInline/SVGInline';
import {
    DarkGradientBottom,
    DarkGradientLeft,
    DarkGradientMiddle,
    DetailButtons,
    DetailButtonsMobile,
    DetailsDescription,
    DetailsDescriptionWrapper,
    DetailsWrapper,
    IconsContainer,
    ImageItem,
    ImageWrapper,
    MetaDataContainer,
    PageHeader,
    ShowMoreButton,
    ShowMoreText,
    ShowMoreTextWrapper,
    SubTitle,
    Title,
    InfoLabelDesktop,
    InfoLabelMobile,
} from './PageDetailsHeader.css';

type HeaderDetails = {
    title: string;
    landscapeImage: string;
    description: string;
    subTitle?: string;
    captionLanguages?: string[] | null;
    originalLanguages?: string[] | null;
    progressBar?: ReactNode;
    metaData?: any[];
    showMoreMetadataTitle?: any[];
    showMoreMetadataContent?: any[];
    cast?: Array<Credit | Person>;
    buttons?: ReactNode[];
    mobileButtons?: ReactNode[];
    channelImage?: ReactNode;
    iconList?: ReactNode[];
    contentMarker?: ReactNode;
    infoMessage?: string;
};

type PropTypes = {
    asset: HeaderDetails;
    overlayActive?: boolean;
    setOverlayActive: (value: boolean) => void;
};

const SHOW_MORE_LINE_THRESHOLD_MOBILE = 3;
const SHOW_MORE_LINE_THRESHOLD = 3;

export const PageDetailHeader: FC<PropTypes> = ({ asset, overlayActive, setOverlayActive }) => {
    const {
        title,
        subTitle,
        description,
        captionLanguages,
        originalLanguages,
        progressBar,
        metaData,
        buttons,
        iconList,
        contentMarker,
        mobileButtons,
        channelImage,
        cast,
        showMoreMetadataTitle,
        showMoreMetadataContent,
        infoMessage,
    } = asset;
    const descriptionRef = useRef(null);
    const [isSmallImage, setIsSmallImage] = useState(false);
    const [isOrientationLandscape, setIsOrientationLandscape] = useState(false);
    const [isImageError, setIsImageError] = useState(false);
    const [showMore, setShowMore] = useState(false);
    const { config } = useConfig();
    const isMounted = useRef(false);
    const { isMobile } = useApp();

    const backgroundImage = asset.landscapeImage;

    const handleSmallImage = () => {
        if (backgroundImage) {
            const image = new Image();

            // eslint-disable-next-line func-names
            image.onload = function () {
                if (isMounted.current) {
                    setIsSmallImage(image.width < config.app_config.miscellanious.detail_page_small_image_threshold);
                    setIsOrientationLandscape(image.width > image.height);
                }
            };
            image.src = backgroundImage;
        }
    };

    const openOverlay = () => {
        setOverlayActive(true);
    };

    const closeOverlay = () => {
        setOverlayActive(false);
    };

    const checkDescription = () => {
        const desc = descriptionRef.current;

        // reset description styles
        if (desc) {
            desc.style = {};
        }

        if (desc?.getBoundingClientRect() && desc?.getBoundingClientRect()?.height > 0) {
            desc.style.display = '-webkit-box';
            desc.style.webkitLineClamp = isMobile() ? SHOW_MORE_LINE_THRESHOLD_MOBILE : SHOW_MORE_LINE_THRESHOLD;
            desc.style.webkitBoxOrient = 'vertical';
            desc.style.overflow = 'hidden';
        }

        setShowMore(description?.length > 0);
    };

    const formatCastAndCrew = () => {
        const director = cast?.filter((person) => person.role === PersonRole.DIRECTOR).map((o) => (o.person ? o.person.name : o.title));
        const actors = cast?.filter((person) => person.role === PersonRole.ACTOR).map((o) => (o.person ? o.person.name : o.title));

        if (!director?.length && !actors?.length) return null;

        const directorStr = director?.length ? `${translate('SCREEN_MORE_INFO_DIRECTORS')} ${director.join(', ')}` : null;
        const actorStr = actors?.length ? `${translate('SCREEN_MORE_INFO_ACTORS')} ${actors.join(', ')}` : null;

        return (
            <>
                <div>{directorStr}</div>
                <div>{actorStr}</div>
            </>
        );
    };

    useEffect(() => {
        if (asset) {
            handleSmallImage();
            checkDescription();
        }
    }, [asset]);

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
        };
    }, []);

    return (
        <>
            {overlayActive && (
                <DetailMoreModal
                    modalTitle={title}
                    modalSubTitle={subTitle}
                    modalText={description}
                    showMoreMetadataTitle={showMoreMetadataTitle}
                    showMoreMetadataContent={showMoreMetadataContent}
                    metadata={metaData}
                    cast={cast?.filter((person) => person.role === PersonRole.ACTOR)}
                    directors={cast?.filter((person) => person.role === PersonRole.DIRECTOR)}
                    original={originalLanguages}
                    caption={captionLanguages}
                    closeOverlay={() => closeOverlay()}
                    iconList={iconList}
                    contentMarker={contentMarker}
                />
            )}
            <PageHeader>
                <ImageWrapper>
                    <ImageItem
                        data-test-id={'tv-detailscreen-backgroundimage'}
                        isSmall={isSmallImage && !isImageError}
                        isOrientationLandscape={isOrientationLandscape}
                    >
                        <PictureWithFallback
                            src={backgroundImage?.replace('http:', 'https:')}
                            fallbackImage={fallbackImage}
                            fit={'cover'}
                            imageError={() => setIsImageError(true)}
                        />
                    </ImageItem>
                    <DarkGradientLeft />
                    <DarkGradientMiddle />
                    <DarkGradientBottom />
                </ImageWrapper>
                <DetailsWrapper data-test-id={'tv-detailscreen-wrapper'} metaData={metaData} description={description}>
                    {channelImage}
                    {!!title && <Title data-test-id={'tv-detailscreen-title'}>{title}</Title>}
                    {subTitle && <SubTitle data-test-id={'tv-detailscreen-subtitle'}>{subTitle}</SubTitle>}
                    <DetailMarkerAndMetaData>
                        <DetailMarkerContainer>
                            {iconList?.length > 0 || contentMarker || metaData.length ? (
                                <IconsContainer iconListLength={iconList?.length} isMobile={isMobile()}>
                                    {contentMarker}
                                    {iconList}
                                    <MetaDataContainer>{metaData.length > 0 && metaData}</MetaDataContainer>
                                </IconsContainer>
                            ) : null}
                        </DetailMarkerContainer>
                    </DetailMarkerAndMetaData>
                    {infoMessage && (
                        <InfoLabelMobile>
                            <SVGInline svg={icons.infoIcon} />
                            {infoMessage}
                        </InfoLabelMobile>
                    )}
                    {(mobileButtons?.length ?? 0) > 0 && <DetailButtonsMobile>{mobileButtons}</DetailButtonsMobile>}
                    {progressBar}
                    {(!!description || !!cast?.length) && (
                        <DetailsDescriptionWrapper showMore={showMore} onClick={showMore ? () => openOverlay() : () => {}}>
                            <DetailsDescription data-test-id={'tv-detailscreen-description'} ref={descriptionRef}>
                                {description || formatCastAndCrew()}
                            </DetailsDescription>
                            {showMore && (
                                <>
                                    <ShowMoreTextWrapper>
                                        <ShowMoreText>{translate('PROGRAM_DETAIL_MORE')}</ShowMoreText>
                                    </ShowMoreTextWrapper>
                                    <ShowMoreButton
                                        type="button"
                                        data-test-id={'tv-detailscreen-showmorebutton'}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openOverlay();
                                        }}
                                        aria-label={translate('SCREEN_DETAIL_MORE_INFO_BUTTON')}
                                    >
                                        <SVGInline svg={icons.showMoreIcon} />
                                    </ShowMoreButton>
                                </>
                            )}
                        </DetailsDescriptionWrapper>
                    )}
                    {infoMessage && (
                        <InfoLabelDesktop>
                            <SVGInline svg={icons.infoIcon} />
                            {infoMessage}
                        </InfoLabelDesktop>
                    )}
                    {buttons.length > 0 && <DetailButtons>{buttons}</DetailButtons>}
                </DetailsWrapper>
            </PageHeader>
        </>
    );
};
